import React, { useState } from 'react';
import { Calendar } from 'lucide-react';

const presets = [
  { id: 'all', label: 'All Time' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'quarter', label: 'Last 90 Days' },
  { id: 'year', label: 'This Year' },
  { id: 'custom', label: 'Custom' },
];

const getPresetRange = (id) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (id) {
    case 'week': {
      const day = today.getDay();
      const start = new Date(today);
      start.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
      return { from: start, to: now };
    }
    case 'month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return { from: start, to: end };
    }
    case 'quarter': {
      const start = new Date(today);
      start.setDate(today.getDate() - 90);
      return { from: start, to: now };
    }
    case 'year':
      return { from: new Date(now.getFullYear(), 0, 1), to: now };
    default:
      return { from: null, to: null };
  }
};

export const filterByDateRange = (items, range, dateField = 'created_at') => {
  if (!range.from && !range.to) return items;
  return items.filter(item => {
    const raw = item[dateField] || item.date || item.created_at || item.dispatch_date || item.start_date;
    if (!raw) return true;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return true;
    if (range.from && d < range.from) return false;
    if (range.to && d > range.to) return false;
    return true;
  });
};

const DateRangeFilter = ({ onChange }) => {
  const [active, setActive] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const handlePreset = (id) => {
    setActive(id);
    if (id === 'custom') return;
    const range = getPresetRange(id);
    onChange(range);
  };

  const handleCustom = () => {
    onChange({
      from: customFrom ? new Date(customFrom + 'T00:00:00') : null,
      to: customTo ? new Date(customTo + 'T23:59:59') : null,
    });
  };

  return (
    <div data-testid="date-range-filter" className="flex items-center gap-2 flex-wrap">
      <Calendar className="w-3.5 h-3.5 text-gray-400" />
      <div className="flex gap-1">
        {presets.map(p => (
          <button key={p.id} onClick={() => handlePreset(p.id)} data-testid={`date-${p.id}`}
            className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${
              active === p.id ? 'text-white' : 'text-gray-500 bg-gray-100 hover:bg-gray-200'
            }`}
            style={active === p.id ? { backgroundColor: '#24549c' } : {}}>
            {p.label}
          </button>
        ))}
      </div>
      {active === 'custom' && (
        <div className="flex items-center gap-1.5" data-testid="custom-date-range-inputs">
          <input type="date" value={customFrom}
            onChange={e => { setCustomFrom(e.target.value); onChange({
              from: e.target.value ? new Date(e.target.value + 'T00:00:00') : null,
              to: customTo ? new Date(customTo + 'T23:59:59') : null,
            }); }}
            className="px-2 py-1 text-[10px] border border-gray-200 rounded-md"
            data-testid="custom-date-from" />
          <span className="text-[10px] text-gray-400">to</span>
          <input type="date" value={customTo}
            onChange={e => { setCustomTo(e.target.value); onChange({
              from: customFrom ? new Date(customFrom + 'T00:00:00') : null,
              to: e.target.value ? new Date(e.target.value + 'T23:59:59') : null,
            }); }}
            className="px-2 py-1 text-[10px] border border-gray-200 rounded-md"
            data-testid="custom-date-to" />
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
